import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Camera,
  Upload,
  Check,
  X,
  Image as ImageIcon,
  FileText,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import insurancePolicyService from "../lib/InsurancePolicyService";
import { useAuth } from "../hooks/useAuth";
import ConfirmModal from "./ConfirmModal";
import {
  useInteractionTracking,
  useErrorTracking,
} from "../hooks/useAnalytics";
import DocumentPreviewModal from "./DocumentPreviewModal";

const DocumentUpload = ({
  userData,
  onComplete,
  onBack,
  onFullApp,
  reupload,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [documentType, setDocumentType] = useState("DEC_Auto");
  const [showSampleModal, setShowSampleModal] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { user, getIdToken, loading: authLoading, logout } = useAuth(); // Get the authenticated user and loading state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [existingPolicy, setExistingPolicy] = useState(null);
  const { trackButtonClick } = useInteractionTracking();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  // Enhanced loading state to show progress steps
  const [uploadState, setUploadState] = useState({
    isLoading: false,
    currentStep: null, // 'uploading', 'processing', 'completing'
    progress: 0,
  });

  // Detect if the user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();

    // Also check on resize in case of orientation changes
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check for existing policies on component mount
  useEffect(() => {
    const checkExistingPolicies = async () => {
      try {
        setCheckingExisting(true);
        const token = await getIdToken();

        // Use the same method as InsurancePolicyUploader
        const result = await insurancePolicyService.checkExistingPolicies(
          token
        );

        if (result) {
          setExistingPolicy(result.policy);

          // If policy exists, complete immediately
          if (onComplete) {
            onComplete(result.policy);
          }
        }
      } catch (err) {
        console.error("Error checking existing policies:", err);
        // Continue with upload flow even if check fails
      } finally {
        setCheckingExisting(false);
      }
    };
    console.log("DOCUMENTUPLOADSCREEN=" + reupload);
    if (reupload) {
      setCheckingExisting(false);
    } else {
      checkExistingPolicies();
    }
  }, []);

  const validateFile = (file) => {
    // Size limit: 10MB
    const MAX_SIZE = 10 * 1024 * 1024;

    // Allow images and PDFs
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      throw new Error("Please upload an image or PDF file");
    }

    if (file.size > MAX_SIZE) {
      throw new Error("File size must be less than 10MB");
    }
  };

  const handleFileSelect = (file) => {
    try {
      validateFile(file);
      setSelectedFile(file);

      // Create preview
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // For PDFs, just show an icon/name (no preview)
        setPreview(null);
      }

      setError(null);
      setUploadSuccess(false);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    try {
      if (!selectedFile) {
        throw new Error("Please select a file first");
      }

      // Initialize upload state
      setUploadState({
        isLoading: true,
        currentStep: "uploading",
        progress: 10,
      });

      setError(null);

      // Get Firebase token for authentication
      setUploadState((prev) => ({ ...prev, progress: 20 }));

      const token = await getIdToken();

      // Start the file upload
      setUploadState((prev) => ({
        ...prev,
        currentStep: "uploading",
        progress: 30,
      }));

      // Process the upload and check-in
      const result = await insurancePolicyService.processPolicyDocument(
        selectedFile,
        token
      );

      if (result.success) {
        // Update progress for processing step
        setUploadState((prev) => ({
          ...prev,
          currentStep: "processing",
          progress: 70,
        }));

        console.log("Upload successful:", result);

        // Store submission info in session storage
        sessionStorage.setItem(
          "documentData",
          JSON.stringify({
            documentType,
            timestamp: new Date().toISOString(),
            fileName: selectedFile.name,
            policyCheckInId: result.result?.id || null,
          })
        );

        // Show completion progress
        setUploadState((prev) => ({
          ...prev,
          currentStep: "completing",
          progress: 100,
        }));

        setUploadSuccess(true);

        // Wait a moment to show success message before completing
        setTimeout(() => {
          if (onComplete) {
            onComplete(result);
          }
        }, 1500);
      } else {
        throw new Error(result.error || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "An unexpected error occurred");
      setUploadState({ isLoading: false, currentStep: null, progress: 0 });
    }
  };

  const handleTryCamera = () => {
    if (isMobile && cameraInputRef.current) {
      cameraInputRef.current.click();
    } else {
      // On desktop where camera might not be reliable, show a helpful message
      setError(
        "Camera works best on mobile devices. You may continue if your device has a camera, or use the gallery option instead."
      );
      setTimeout(() => {
        if (cameraInputRef.current) {
          cameraInputRef.current.click();
        }
      }, 1500);
    }
  };
  if (checkingExisting) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          <span className="ml-3">Checking for existing documents...</span>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6 p-4">
      {/* Full-screen loading overlay */}
      {uploadState.isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center w-80">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${uploadState.progress}%` }}
              ></div>
            </div>

            <p className="font-medium text-gray-900">
              {uploadState.currentStep === "uploading" &&
                "Uploading your document..."}
              {uploadState.currentStep === "processing" &&
                "Processing insurance details..."}
              {uploadState.currentStep === "completing" && "Almost done..."}
            </p>

            <p className="text-sm text-gray-500 mt-2">
              Please don't close this window
            </p>
          </div>
        </div>
      )}

      {/* Back button if onBack is provided */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to options
        </button>
      )}

      {/* Progress Overview */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 mt-1" />
            <div>
              <p className="font-medium text-blue-900">Final Step!</p>
              <p className="text-sm text-blue-700">
                Upload your insurance document to receive your Ownli wallet
                reward
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Document Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            Declaration Page (Auto){" "}
            <a
              onClick={() => setOpenPreviewModal(true)}
              className="underline text-[#1B1B1E]"
            >
              See a sample
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Document</CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedFile ? (
            <div className="space-y-4">
              {/* File Input (hidden) */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInput}
                accept="image/*,application/pdf"
                className="hidden"
              />
              {/* Camera Input (hidden) */}
              <input
                type="file"
                ref={cameraInputRef}
                onChange={handleFileInput}
                accept="image/*"
                capture="environment"
                className="hidden"
              />
              {/* Upload Options */}
              <div
                className={
                  isMobile ? "grid grid-cols-2 gap-4" : "flex justify-center"
                }
              >
                <button
                  onClick={() => {
                    trackButtonClick("file_upload", {
                      step_name: "document_upload",
                      user_id: userData?.id,
                    });
                    fileInputRef.current?.click();
                  }}
                  className="flex flex-col items-center gap-2 p-6 border-2 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <ImageIcon className="w-8 h-8 text-blue-500" />
                  <span className="text-sm font-medium">Choose from Files</span>
                </button>

                {isMobile && (
                  <button
                    onClick={() => {
                      trackButtonClick("camera", {
                        step_name: "document_upload",
                        user_id: userData?.id,
                      });
                      handleTryCamera();
                    }}
                    className="flex flex-col items-center gap-2 p-6 border-2 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <Camera className="w-8 h-8 text-blue-500" />
                    <span className="text-sm font-medium">Take Photo</span>
                  </button>
                )}
              </div>
              {isMobile ? (
                <p className="text-sm text-gray-500 text-center mt-2">
                  You can take a photo or upload from your gallery
                </p>
              ) : (
                <p className="text-sm text-gray-500 text-center mt-2">
                  Upload an image or PDF document (max 10MB)
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Preview */}
              <div className="relative">
                {preview ? (
                  // Image preview
                  <img
                    src={preview}
                    alt="Selected file preview"
                    className="w-full rounded-lg"
                  />
                ) : (
                  // PDF preview (just show an icon and filename)
                  <div className="p-8 border rounded-lg bg-gray-50 flex flex-col items-center justify-center">
                    <FileText className="w-12 h-12 text-blue-500 mb-2" />
                    <span className="font-medium text-gray-900 break-all">
                      {selectedFile.name}
                    </span>
                    <span className="text-sm text-gray-500 mt-1">
                      PDF Document
                    </span>
                  </div>
                )}
                <button
                  onClick={() => {
                    trackButtonClick("upload", {
                      step_name: "document_upload",
                      user_id: userData?.id,
                    });
                    setShowConfirmModal(true);
                  }}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
                <ConfirmModal
                  open={showConfirmModal}
                  onOpenChange={setShowConfirmModal}
                  onConfirm={() => {
                    setSelectedFile(null);
                    setPreview(null);
                    setShowConfirmModal(false);
                  }}
                />
              </div>

              {/* Success message */}
              {uploadSuccess && (
                <div className="flex items-start gap-2 p-3 bg-green-50 text-green-800 rounded-lg text-sm">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>Document uploaded successfully!</p>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={uploadState.isLoading}
                className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient !mt-10 text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors ${
                  uploadState.isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {uploadState.isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Upload Document</span>
                  </>
                )}
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      {/* Full App Option */}
      {/* {onFullApp && (
        <button
          onClick={onFullApp}
          className="w-full text-center text-gray-600 hover:text-gray-900 text-sm"
        >
          Switch to full Ownli experience
        </button>
      )} */}
      <DocumentPreviewModal
        open={openPreviewModal}
        onOpenChange={setOpenPreviewModal}
      />
    </div>
  );
};

export default DocumentUpload;
